import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SectionHead } from "@/components/site/Motion";
import { assetUrl } from "@/lib/api";

function Lightbox({ items, index, setIndex }) {
  const open = index !== null;
  const go = (d) => setIndex((i) => (i + d + items.length) % items.length);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const item = open ? items[index] : null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && setIndex(null)}>
      <DialogContent className="max-w-5xl w-[calc(100%-1.5rem)] p-0 bg-ink border-0 overflow-hidden [&>button]:hidden" data-testid="gallery-lightbox">
        <DialogTitle className="sr-only">{item?.caption || "Photo"}</DialogTitle>
        {item && <img src={assetUrl(item.src)} alt={item.caption || ""} className="w-full max-h-[80vh] object-contain bg-ink" />}
        <div className="flex items-center justify-between gap-3 p-3 text-cream">
          <button onClick={() => go(-1)} className="grid place-items-center h-10 w-10 rounded-full bg-cream/10 hover:bg-cream hover:text-ink" aria-label="Previous photo" data-testid="lightbox-prev-btn"><ChevronLeft className="h-5 w-5" /></button>
          <p className="text-sm text-cream/85 truncate">{item?.caption} <span className="font-mono text-cream/60">{open ? `${index + 1} / ${items.length}` : ""}</span></p>
          <div className="flex gap-2">
            <button onClick={() => go(1)} className="grid place-items-center h-10 w-10 rounded-full bg-cream/10 hover:bg-cream hover:text-ink" aria-label="Next photo" data-testid="lightbox-next-btn"><ChevronRight className="h-5 w-5" /></button>
            <button onClick={() => setIndex(null)} className="grid place-items-center h-10 w-10 rounded-full bg-cream/10 hover:bg-cream hover:text-ink" aria-label="Close" data-testid="lightbox-close-btn"><X className="h-5 w-5" /></button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Swipeable photo strip (native swipe / trackpad, arrows wrap around) with a tap-to-enlarge lightbox. */
export function PageGallery({ items, k, name }) {
  const track = useRef(null);
  const [open, setOpen] = useState(null);
  if (!items.length) return null;

  const step = (d) => {
    const el = track.current;
    if (!el) return;
    const card = el.firstElementChild?.getBoundingClientRect().width ?? 300;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
    const atStart = el.scrollLeft <= 8;
    if (d > 0 && atEnd) el.scrollTo({ left: 0, behavior: "smooth" });
    else if (d < 0 && atStart) el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    else el.scrollBy({ left: d * (card + 16), behavior: "smooth" });
  };
  const arrow = "grid place-items-center h-12 w-12 rounded-full border border-ink/15 hover:bg-ink hover:text-cream transition-colors";

  return (
    <section className="bg-paper py-24 sm:py-32 overflow-hidden" data-testid="destination-gallery">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead k={`${k}.gallery`} eyebrow="Gallery" title={[`${name},`, "in pictures."]}>
          <div className="flex gap-2">
            <button onClick={() => step(-1)} className={arrow} aria-label="Previous photos" data-testid="gallery-prev-btn"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={() => step(1)} className={arrow} aria-label="Next photos" data-testid="gallery-next-btn"><ChevronRight className="h-5 w-5" /></button>
          </div>
        </SectionHead>
      </div>
      <div ref={track} className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar px-5 sm:px-8 lg:px-[max(2rem,calc((100vw-80rem)/2+2rem))] pb-2" data-testid="gallery-track">
        {items.map((g, i) => (
          <button key={`${g.src}-${i}`} onClick={() => setOpen(i)} data-testid={`gallery-item-${i}`}
            className="group relative shrink-0 snap-start w-[78vw] sm:w-[46vw] lg:w-[30rem] aspect-[4/3] overflow-hidden rounded-[24px] text-left">
            <img src={assetUrl(g.src)} alt={g.caption || `${name} photo ${i + 1}`} loading="lazy" draggable={false}
              className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" />
            {g.caption && <span className="absolute left-3 bottom-3 eyebrow bg-paper/90 text-ink rounded-full px-3 py-1">{g.caption}</span>}
          </button>
        ))}
      </div>
      <Lightbox items={items} index={open} setIndex={setOpen} />
    </section>
  );
}
